/**
 * Pick Up Safety Alerts Page
 * Dashboard for managing pickup safety notifications
 * Includes SMS subscription, settings, bulk actions, and activity logs
 */
import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { 
  ShieldCheck, Mail, Phone, Check, Lock, AlertTriangle, Copy, ExternalLink,
  TrendingUp, Users, Clock, Download, CreditCard, Loader2,
  ChevronRight, ChevronDown, ChevronUp, DollarSign, Calendar, History, Settings, Printer, FileSpreadsheet, CheckCircle2,
  Send, Receipt, FileText, Bell, Info, AlertCircle, RefreshCw, Archive, Trash2, RotateCcw
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import PageHero, { HERO_IMAGES } from "@/components/common/PageHero";
import FeatureGate from "@/components/FeatureGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import SquareSetupGuide from "@/components/shared/SquareSetupGuide";

// Square subscription links for SMS Alerts
const SQUARE_SMS_SUBSCRIPTION_LINKS = {
  monthly: "https://square.link/u/S2TEiDcg",  // $14.99/month (50% off)
  annual: "https://square.link/u/TBVXTRSG"     // $161.80/year
};

// ── OPTION A: Director Notice Panel — What parents will receive ──
function DirectorNoticePanel({ orgName }) {
  const [expanded, setExpanded] = useState(false);
  const shortName = (orgName || 'Your Center').slice(0, 12);

  return (
    <div className="rounded-xl border-2 border-amber-300 bg-amber-50 overflow-hidden">
      <button type="button" onClick={() => setExpanded(p => !p)}
        className="w-full flex items-center justify-between px-5 py-3 bg-amber-100 hover:bg-amber-200 transition-colors">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-amber-700 flex-shrink-0" />
          <span className="font-bold text-amber-800 text-sm">Important: What parents receive — Read before subscribing</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-amber-600" /> : <ChevronDown className="w-4 h-4 text-amber-600" />}
      </button>

      {expanded && (
        <div className="px-5 py-4 space-y-4">

          {/* What this service does */}
          <div className="bg-white rounded-xl border border-amber-200 p-4">
            <p className="font-bold text-gray-800 mb-2 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              What This Service Does
            </p>
            <p className="text-sm text-gray-600">
              When someone <strong>other than the primary pickup person</strong> signs out a child, the parent/guardian 
              automatically receives an instant alert with the pickup person's name and exact time. 
              This is an extra safety layer — parents always know who picked up their child.
            </p>
          </div>

          {/* Exact SMS parents receive */}
          <div className="bg-white rounded-xl border border-blue-200 p-4">
            <p className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Phone className="w-4 h-4 text-blue-600" />
              Exact SMS Alert Parents Receive
            </p>
            <div className="bg-gray-900 rounded-xl p-4 font-mono text-sm text-green-400 leading-relaxed">
              <p className="text-gray-400 text-xs mb-2">📱 Text Message — from your center</p>
              <p>"{shortName}: [Child Name] picked up by [Pickup Person] at [Time].</p>
              <p className="mt-1">Reply STOP to opt out. Msg&data rates may apply."</p>
            </div>
            <p className="text-xs text-gray-400 mt-2">The center name, child name, pickup person, and time are all filled in automatically at the moment of pickup.</p>
          </div>

          {/* Welcome email */}
          <div className="bg-white rounded-xl border border-purple-200 p-4">
            <p className="font-bold text-gray-800 mb-2 flex items-center gap-2">
              <Mail className="w-4 h-4 text-purple-600" />
              Welcome Email — Sent Automatically When You Enable Alerts Per Child
            </p>
            <div className="bg-purple-50 rounded-lg p-3 text-sm text-purple-800 space-y-1">
              <p><strong>Subject:</strong> 🛡️ Pick Up Safety Alerts Enabled for [Child Name]</p>
              <p className="text-xs text-purple-600 mt-2">The email tells parents:</p>
              <ul className="text-xs text-purple-700 space-y-1 ml-3">
                <li>✓ What triggers an alert (alternate pickup person)</li>
                <li>✓ What information is in each notification (who + time)</li>
                <li>✓ That alerts only fire for authorized persons on their child's list</li>
                <li>✓ To contact your center with any questions</li>
              </ul>
            </div>
          </div>

          {/* Twilio compliance */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="font-bold text-gray-800 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              Twilio SMS Compliance (Required)
            </p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Parents can reply <strong>STOP</strong> at any time to stop all alerts</li>
              <li>• Parents can reply <strong>START</strong> to re-enable alerts</li>
              <li>• Every message includes the required compliance footer</li>
              <li>• Opt-outs are permanently logged — compliant with A2P 10DLC regulations</li>
            </ul>
          </div>

          {/* Script for explaining to parents */}
          <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4">
            <p className="font-bold text-emerald-800 mb-2 flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-600" />
              How to Explain This to Parents at Enrollment
            </p>
            <div className="bg-white rounded-lg p-3 text-sm text-gray-700 italic border border-emerald-100">
              <p>"We use a Pick Up Safety Alert service. Whenever someone other than your primary pickup person signs out your child, 
              you'll automatically receive a text message with their name and the exact time. 
              You'll also receive a welcome email explaining everything. 
              Standard message and data rates may apply. You can reply STOP at any time to opt out."</p>
            </div>
          </div>

        </div>
      )}
    </div>
  );
}




// ── Pickup Safety Billing Invoice Panel — mirrors Parent Communication billing ──
function PickupBillingInvoicePanel({ billingSettings, billableChildren }) {
  const [children, setChildren] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [records, setRecords] = useState([]);
  const [sending, setSending] = useState(false);
  const [billingNote, setBillingNote] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loadingRecords, setLoadingRecords] = useState(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => { buildChildren(); fetchRecords(); }, [billableChildren, billingSettings]);

  const buildChildren = () => {
    // Build list from billableChildren — each child's fee auto-calculated from toggle state
    const emailFee = parseFloat(billingSettings?.emailFeePerChild) || 0;
    const smsFee = parseFloat(billingSettings?.smsFeePerChild) || 0;

    const list = (billableChildren?.allChildren || []).map(c => {
      const emailActive = c.pickupAlerts?.emailAlertEnabled || false;
      const smsActive = c.pickupAlerts?.smsAlertEnabled || false;
      const fee = (emailActive ? emailFee : 0) + (smsActive ? smsFee : 0);
      const p = c.pickupPersons?.[0] || {};
      const parentName = (p.name || `${p.firstName||''} ${p.lastName||''}`.trim()) || 'Unknown Parent';
      const phone = p.phone || p.cellPhone || '';
      return { childName: c.name || 'Unknown', parentName, phone, email: p.email || '', childId: c.id, fee, emailActive, smsActive };
    }).filter(c => c.phone && c.fee > 0); // Only show children with active alerts and a phone number

    setChildren(list);
    setSelected(new Set()); // Reset selection when data changes
  };

  const fetchRecords = async () => {
    setLoadingRecords(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/pickup-safety/sent-billing-records`);
      setRecords(res.data?.records || []);
    } catch {} finally { setLoadingRecords(false); }
  };

  const toggleAll = () => selected.size === children.length ? setSelected(new Set()) : setSelected(new Set(children.map((_, i) => i)));

  const handleSend = async () => {
    if (!selected.size) return;
    // Group by parent phone — one invoice per parent
    const byParent = {};
    [...selected].forEach(i => {
      const c = children[i];
      if (!byParent[c.phone]) byParent[c.phone] = { parentName: c.parentName, phone: c.phone, email: c.email, items: [] };
      byParent[c.phone].items.push({ childName: c.childName, amountCents: Math.round(c.fee * 100) });
    });
    const recipients = Object.values(byParent).map(p => ({
      parentName: p.parentName, phone: p.phone, email: p.email,
      childName: p.items.map(i => i.childName).join(', '),
      amountCents: p.items.reduce((s, i) => s + i.amountCents, 0),
    }));
    setSending(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/pickup-safety/send-billing-invoices`, { recipients, billingNote, feePassthrough: billingSettings?.feePassthrough !== false });
      toast.success(res.data.message);
      setSelected(new Set()); setBillingNote(''); fetchRecords();
    } catch { toast.error("Failed to send invoices"); } finally { setSending(false); }
  };

  const markPaid = async (id) => {
    try {
      await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/pickup-safety/sent-billing-records/${id}/paid`);
      toast.success("Marked as paid"); fetchRecords();
    } catch {}
  };

  const filtered = records.filter(r => statusFilter === 'all' || r.status === statusFilter);
  const totalOwed = records.filter(r => r.status === 'pending').reduce((s, r) => s + (r.amountCents || 0), 0);
  const totalPaid = records.filter(r => r.status === 'paid').reduce((s, r) => s + (r.amountCents || 0), 0);

  return (
    <div className="space-y-4 mt-4">
      {/* Send Invoices — purple, matches Parent SMS Comm exactly */}
      <Card className="border-2 border-purple-300 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-700 rounded-t-xl">
          <CardTitle className="flex items-center gap-2 text-white">
            <Send className="w-5 h-5" />Send Invoices
          </CardTitle>
          <p className="text-sm text-purple-100">
            Fees auto-calculated per child based on active alerts · {children.length} billable children
          </p>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={selected.size === children.length && children.length > 0} onChange={toggleAll} className="w-4 h-4 accent-purple-600" />
              <span className="text-sm font-medium text-gray-700">Select All ({children.length} children)</span>
            </label>
            <span className="text-xs text-gray-500">
              {selected.size} selected · Total: ${[...selected].reduce((s, i) => s + (children[i]?.fee || 0), 0).toFixed(2)}
            </span>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {children.length === 0
              ? <p className="text-sm text-gray-400 italic text-center py-4">No children with active alerts and phone numbers. Enable email/SMS alerts per child above.</p>
              : children.map((c, i) => (
                <label key={i} className={`flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border-2 cursor-pointer transition-all ${selected.has(i) ? 'border-purple-500 bg-purple-50' : 'border-gray-200 bg-white hover:border-purple-300'}`}>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={selected.has(i)} onChange={() => { const n = new Set(selected); n.has(i) ? n.delete(i) : n.add(i); setSelected(n); }} className="w-4 h-4 accent-purple-600" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">{c.childName}</p>
                      <p className="text-xs text-gray-400">
                        Parent: {c.parentName} · {c.phone}
                        <span className="ml-2">
                          {c.emailActive && <span className="text-blue-500">Email </span>}
                          {c.smsActive && <span className="text-green-500">SMS </span>}
                        </span>
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-purple-700">${c.fee.toFixed(2)}</span>
                </label>
              ))}
          </div>

          <div>
            <label className="text-xs text-gray-500 font-medium">Optional Note to Parents</label>
            <input value={billingNote} onChange={e => setBillingNote(e.target.value)}
              placeholder="e.g. Due by the 5th of the month"
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
          </div>

          <Button onClick={handleSend} disabled={sending || selected.size === 0}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-700 hover:to-indigo-800 text-white py-5 font-semibold rounded-xl shadow">
            {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Send Invoice to {selected.size} Parent{selected.size !== 1 ? 's' : ''}</>}
          </Button>
        </CardContent>
      </Card>

      {/* Payment Tracker — dark, matches Parent SMS Comm exactly */}
      <Card className="border-2 border-gray-200 shadow-sm">
        <CardHeader className="bg-gradient-to-r from-gray-700 to-gray-800 rounded-t-xl">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2 text-white"><DollarSign className="w-5 h-5" />Payment Tracker</CardTitle>
            <div className="flex gap-3 text-sm">
              <span className="text-green-300 font-semibold">Paid: ${(totalPaid/100).toFixed(2)}</span>
              <span className="text-amber-300 font-semibold">Pending: ${(totalOwed/100).toFixed(2)}</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-3">
          <div className="flex gap-2">
            {['all','pending','paid'].map(f => (
              <button key={f} type="button" onClick={() => setStatusFilter(f)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold border capitalize transition-all ${statusFilter === f ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500'}`}>{f}</button>
            ))}
            <button onClick={fetchRecords} className="ml-auto p-1.5 text-gray-400 hover:text-gray-600 rounded-lg border border-gray-200"><RefreshCw className="w-4 h-4" /></button>
          </div>
          {loadingRecords ? <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          : filtered.length === 0 ? <p className="text-sm text-gray-400 italic text-center py-6">No billing records yet.</p>
          : <div className="overflow-x-auto"><table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-200"><tr>
                <th className="px-3 py-2 text-left text-gray-600 font-semibold">Parent</th>
                <th className="px-3 py-2 text-left text-gray-600 font-semibold">Children</th>
                <th className="px-3 py-2 text-left text-gray-600 font-semibold">Amount</th>
                <th className="px-3 py-2 text-left text-gray-600 font-semibold">Sent</th>
                <th className="px-3 py-2 text-left text-gray-600 font-semibold">Status</th>
                <th className="px-3 py-2"></th>
              </tr></thead>
              <tbody>{filtered.map((r, i) => (
                <tr key={r.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-2 font-medium text-gray-800">{r.parentName}</td>
                  <td className="px-3 py-2 text-gray-500">{(r.children||[]).join(', ')}</td>
                  <td className="px-3 py-2 font-bold">${((r.amountCents||0)/100).toFixed(2)}</td>
                  <td className="px-3 py-2 text-gray-500">{r.sentAt ? new Date(r.sentAt).toLocaleDateString() : '—'}</td>
                  <td className="px-3 py-2">{r.status === 'paid'
                    ? <span className="flex items-center gap-1 text-green-600 font-semibold"><CheckCircle2 className="w-3.5 h-3.5" />Paid{r.paidVia === 'square' ? <span className="ml-1 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold">Square</span> : r.paidVia === 'manual' ? <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-semibold">Manual</span> : null}</span>
                    : <span className="flex items-center gap-1 text-amber-600"><Clock className="w-3.5 h-3.5" />Pending</span>}
                  </td>
                  <td className="px-3 py-2">{r.status === 'pending' && <button onClick={() => markPaid(r.id)} className="text-xs bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded-lg">Mark Paid</button>}</td>
                </tr>
              ))}</tbody>
            </table></div>}
        </CardContent>
      </Card>
    </div>
  );
}


export default function PickupSafetyPage() {
  const { user, organization, token } = useAuth();
  const PRIMARY_OWNER_EMAIL = "todayskidz@comcast.net";
  const isPrimaryOwner = organization?.isOwnerAccount === true || user?.email?.toLowerCase() === PRIMARY_OWNER_EMAIL.toLowerCase();
  const subscriptionSectionRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [settings, setSettings] = useState({
    emailSubscribed: true,
    smsSubscribed: false,
    smsSubscribedAt: null,
    smsSubscriptionEndDate: null,
    smsSubscriptionId: null,
    defaultEmailAlertEnabled: false,
    defaultSmsAlertEnabled: false,
    totalChildrenWithEmailAlert: 0,
    totalChildrenWithSmsAlert: 0
  });
  const [stats, setStats] = useState({    emailsSent: 0,
    smsSent: 0,
    childrenWithEmailAlert: 0,
    childrenWithSmsAlert: 0,
    totalChildren: 0
  });
  const [logs, setLogs] = useState([]);
  // Notification History — Archive UI state
  const [logsView, setLogsView] = useState("active"); // 'active' | 'archived'
  const [logsMonth, setLogsMonth] = useState(""); // '' = all time; else 'YYYY-MM'
  const [logsCounts, setLogsCounts] = useState({ total: 0, active: 0, archived: 0 });
  const [logActionLoading, setLogActionLoading] = useState(null); // log id currently being acted on
  const [clearingArchived, setClearingArchived] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("monthly");
  const [bulkLoading, setBulkLoading] = useState(false);
  
  // Parent billing settings
  const [estimateN, setEstimateN] = useState('');
  const [billingSettings, setBillingSettings] = useState({
    chargeParents: false,
    emailFeePerChild: 0,
    smsFeePerChild: 0,
    billingFrequency: 'monthly',
    billingDay: 1,
    billingMode: 'auto',
    feePassthrough: true,
    zelleEmail: '',
    zellePhone: '',
    parentSquareEnabled: false,
  });
  const [billableChildren, setBillableChildren] = useState({ allChildren: [], emailAlertChildren: [], smsAlertChildren: [] });
  // Verify SMS Setup (Send Test Pickup SMS) state
  const [testSmsPhone, setTestSmsPhone] = useState("");
  const [sendingTestSms, setSendingTestSms] = useState(false);
  const [showBillableList, setShowBillableList] = useState(false);
  const [billedChildren, setBilledChildren] = useState({});
  const [selectedForBilling, setSelectedForBilling] = useState(new Set());
  const [paymentLinks, setPaymentLinks] = useState({});
  const [creatingLink, setCreatingLink] = useState(null);
  const [squareConnected, setSquareConnected] = useState(false);
  const [connectingSquare, setConnectingSquare] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [savingBilling, setSavingBilling] = useState(false);
  const [billingHistory, setBillingHistory] = useState([]);
  const [showBillingHistory, setShowBillingHistory] = useState(false);
  const [generatingInvoices, setGeneratingInvoices] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [unpaidInvoices, setUnpaidInvoices] = useState({ invoices: [], total: 0, totalAmount: 0 });
  const [invoiceDueDate, setInvoiceDueDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 14); // Default 14 days from now
    return date.toISOString().split('T')[0];
  });

  useEffect(() => {
    if (user?.organizationId) {
      fetchData();
    }
  }, [user?.organizationId]);

  const canActivateParentBilling = useMemo(
    () => Boolean(settings.smsSubscribed || settings.permanentAccess),
    [settings.permanentAccess, settings.smsSubscribed]
  );

  const fetchData = async () => {
    try {
      setLoading(true);
      // Check for preview_mode in URL params
      const urlParams = new URLSearchParams(window.location.search);
      const previewMode = urlParams.get('preview_mode');
      const previewParam = previewMode ? `&preview_mode=${previewMode}` : '';
      
      const [settingsRes, statsRes, logsRes] = await Promise.all([
        axios.get(`${API}/pickup-safety/settings?organizationId=${user.organizationId}${previewParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/pickup-safety/stats?organizationId=${user.organizationId}`),
        axios.get(`${API}/pickup-safety/logs?organizationId=${user.organizationId}&limit=500&archived=false`)
      ]);
      
      setSettings(settingsRes.data);
      setStats(statsRes.data);
      setLogs(logsRes.data.logs || []);
      if (logsRes.data.counts) setLogsCounts(logsRes.data.counts);
      
      // Load billing settings if they exist
      if (settingsRes.data.parentBilling) {
        setBillingSettings(prev => ({ ...prev, ...settingsRes.data.parentBilling }));
      }

      // Check Square connection
      try {
        const sqRes = await axios.get(`${API}/square/status`);
        setSquareConnected(sqRes.data.connected || false);
      } catch {}

    } catch (error) {
      console.error("Error fetching pickup safety data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
      // Auto-load children list
      try { await fetchBillableChildren(); } catch(e) {}
    }
  };

  // ── Notification History: archive helpers ──
  const fetchLogs = async (view = logsView, month = logsMonth) => {
    try {
      const monthParam = month ? `&month=${month}` : '';
      const archivedParam = view === 'archived' ? 'true' : 'false';
      const res = await axios.get(
        `${API}/pickup-safety/logs?organizationId=${user.organizationId}&limit=500&archived=${archivedParam}${monthParam}`
      );
      setLogs(res.data.logs || []);
      if (res.data.counts) setLogsCounts(res.data.counts);
    } catch (err) {
      console.error('Failed to fetch logs', err);
      toast.error('Failed to load notification history');
    }
  };

  const handleArchiveLog = async (logId) => {
    try {
      setLogActionLoading(logId);
      await axios.post(`${API}/pickup-safety/logs/${logId}/archive`, {
        organizationId: user.organizationId,
        userId: user.id || user._id || null,
      });
      toast.success('Notification archived');
      await fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive');
    } finally {
      setLogActionLoading(null);
    }
  };

  const handleRestoreLog = async (logId) => {
    try {
      setLogActionLoading(logId);
      await axios.post(`${API}/pickup-safety/logs/${logId}/restore`, {
        organizationId: user.organizationId,
        userId: user.id || user._id || null,
      });
      toast.success('Notification re-established');
      await fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore');
    } finally {
      setLogActionLoading(null);
    }
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm('Permanently delete this notification log? This cannot be undone.')) return;
    try {
      setLogActionLoading(logId);
      await axios.delete(`${API}/pickup-safety/logs/${logId}?organizationId=${user.organizationId}`);
      toast.success('Notification permanently deleted');
      await fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setLogActionLoading(null);
    }
  };

  const handleClearAllArchived = async () => {
    if (!window.confirm('Permanently delete ALL archived notification logs? This cannot be undone.')) return;
    try {
      setClearingArchived(true);
      const res = await axios.delete(
        `${API}/pickup-safety/logs/clear-archived?organizationId=${user.organizationId}`
      );
      toast.success(`Deleted ${res.data.deleted || 0} archived log${(res.data.deleted || 0) !== 1 ? 's' : ''}`);
      await fetchLogs('archived', logsMonth);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clear archived');
    } finally {
      setClearingArchived(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      setSubscribing(true);
      // Store pending subscription info then redirect to Square subscription checkout
      await axios.post(`${API}/pickup-safety/pending-subscription`, {
        organizationId: user.organizationId,
        billingCycle: selectedPlan,
        initiatedAt: new Date().toISOString()
      });
      const subscriptionLink = selectedPlan === 'annual'
        ? SQUARE_SMS_SUBSCRIPTION_LINKS.annual
        : SQUARE_SMS_SUBSCRIPTION_LINKS.monthly;
      window.location.href = subscriptionLink;
    } catch (error) {
      console.error("Error starting subscription:", error);
      toast.error("Failed to start checkout. Please try again.");
      setSubscribing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Are you sure you want to cancel your SMS subscription? You'll keep access until the end of your current billing period.")) {
      return;
    }
    
    try {
      setCanceling(true);
      
      await axios.post(`${API}/pickup-safety/cancel-subscription`, {
        organizationId: user.organizationId
      });
      
      toast.success("Subscription canceled. You'll have access until the end of your billing period.");
      fetchData(); // Refresh to update status
    } catch (error) {
      console.error("Error canceling subscription:", error);
      toast.error("Failed to cancel subscription. Please try again or contact support.");
    } finally {
      setCanceling(false);
    }
  };

  const handleToggleEmail = async (enabled) => {
    try {
      await axios.post(`${API}/pickup-safety/toggle-email?organizationId=${user.organizationId}&enabled=${enabled}`);
      setSettings(prev => ({ ...prev, emailSubscribed: enabled }));
      toast.success(`Email alerts ${enabled ? 'enabled' : 'disabled'}`);
      if (!enabled) {
        fetchData(); // Refresh to update counts
      }
    } catch (error) {
      console.error("Error toggling email alerts:", error);
      toast.error("Failed to update email settings");
    }
  };

  const handleSaveBillingSettings = async () => {
    try {
      await axios.post(`${API}/pickup-safety/parent-billing?organizationId=${user.organizationId}`, billingSettings);
      toast.success("Parent billing settings saved");
      // Reload settings so Send Invoice panel reflects new fees immediately
      await fetchData();
    } catch (error) {
      console.error("Error saving billing settings:", error);
      toast.error("Failed to save billing settings");
    }
  };

  const handleBillingChange = (field, value) => {
    setBillingSettings(prev => ({ ...prev, [field]: value }));
  };

  const scrollToSubscriptionSection = () => {
    subscriptionSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const fetchBillableChildren = async () => {
    try {
      const [childrenRes, billingRes, unpaidRes, linksRes] = await Promise.all([
        axios.get(`${API}/pickup-safety/billable-children?organizationId=${user.organizationId}`),
        axios.get(`${API}/pickup-safety/billing-records?organizationId=${user.organizationId}&billingPeriod=${billingPeriod}`),
        axios.get(`${API}/pickup-safety/unpaid-invoices?organizationId=${user.organizationId}`),
        axios.get(`${API}/pickup-safety/child-payment-links`),
      ]);
      setBillableChildren(childrenRes.data);
      setBilledChildren(billingRes.data.billedMap || {});
      setUnpaidInvoices(unpaidRes.data);
      setPaymentLinks(linksRes.data.links || {});
      setSelectedForBilling(new Set());
      setShowBillableList(true);
    } catch (error) {
      console.error("Error fetching billable children:", error);
      toast.error("Failed to load billable children");
    }
  };

  const toggleChildAlert = async (childId, alertType, currentValue, child) => {
    try {
      // Preserve the OTHER alert's current state when toggling one
      const updatePayload = alertType === 'email'
        ? { emailAlertEnabled: !currentValue, smsAlertEnabled: child.pickupAlerts?.smsAlertEnabled || false }
        : { emailAlertEnabled: child.pickupAlerts?.emailAlertEnabled || false, smsAlertEnabled: !currentValue };
      await axios.put(`${API}/pickup-safety/child/${childId}`, updatePayload);
      await fetchBillableChildren();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to update alert setting");
    }
  };

  const connectSquare = async () => {
    setConnectingSquare(true);
    try {
      const res = await axios.get(`${API}/square/authorize`);
      window.location.href = res.data.authorization_url;
    } catch {
      toast.error("Unable to start Square connection");
      setConnectingSquare(false);
    }
  };

  const savePaymentOptions = async () => {
    try {
      await axios.post(`${API}/pickup-safety/parent-billing?organizationId=${user.organizationId}`, billingSettings);
      toast.success("Payment options saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to save");
    }
  };

  const createPaymentLink = async (childId, alertType) => {
    const key = `${childId}_${alertType}`;
    setCreatingLink(key);
    try {
      const res = await axios.post(`${API}/pickup-safety/child-payment-link`, { childId, alertType });
      setPaymentLinks(prev => ({ ...prev, [key]: { linkUrl: res.data.linkUrl, status: "pending" } }));
      toast.success("Payment link created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to create link");
    } finally {
      setCreatingLink(null);
    }
  };

  // Reload billing records when period changes
  const handlePeriodChange = async (newPeriod) => {
    setBillingPeriod(newPeriod);
    if (showBillableList) {
      try {
        const billingRes = await axios.get(`${API}/pickup-safety/billing-records?organizationId=${user.organizationId}&billingPeriod=${newPeriod}`);
        setBilledChildren(billingRes.data.billedMap || {});
        setSelectedForBilling(new Set());
      } catch (error) {
        console.error("Error fetching billing records:", error);
      }
    }
  };

  const toggleBilledStatus = async (childId, alertType) => {
    const key = `${childId}_${alertType}`;
    const isCurrentlyBilled = !!billedChildren[key];
    
    try {
      setSavingBilling(true);
      
      if (isCurrentlyBilled) {
        // Remove billing record
        await axios.delete(`${API}/pickup-safety/billing-records?organizationId=${user.organizationId}&childId=${childId}&alertType=${alertType}&billingPeriod=${billingPeriod}`);
        setBilledChildren(prev => {
          const newMap = { ...prev };
          delete newMap[key];
          return newMap;
        });
        toast.success('Marked as not billed');
      } else {
        // Add billing record
        await axios.post(`${API}/pickup-safety/billing-records?organizationId=${user.organizationId}&userId=${user.id}`, {
          childIds: [childId],
          alertType: alertType,
          billingPeriod: billingPeriod
        });
        setBilledChildren(prev => ({
          ...prev,
          [key]: {
            childId,
            alertType,
            billingPeriod,
            billedAt: new Date().toISOString()
          }
        }));
        toast.success('Marked as billed');
      }
    } catch (error) {
      console.error("Error updating billing status:", error);
      toast.error("Failed to update billing status");
    } finally {
      setSavingBilling(false);
    }
  };

  const toggleSelectForBilling = (childId, alertType) => {
    const key = `${childId}_${alertType}`;
    setSelectedForBilling(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const handleMarkSelectedAsBilled = async () => {
    if (selectedForBilling.size === 0) {
      toast.info('No children selected');
      return;
    }
    
    try {
      setSavingBilling(true);
      
      // Group by alert type
      const emailChildIds = [];
      const smsChildIds = [];
      
      selectedForBilling.forEach(key => {
        const [childId, alertType] = key.split('_');
        if (alertType === 'email') {
          emailChildIds.push(childId);
        } else {
          smsChildIds.push(childId);
        }
      });
      
      const promises = [];
      if (emailChildIds.length > 0) {
        promises.push(axios.post(`${API}/pickup-safety/billing-records?organizationId=${user.organizationId}&userId=${user.id}`, {
          childIds: emailChildIds,
          alertType: 'email',
          billingPeriod: billingPeriod
        }));
      }
      if (smsChildIds.length > 0) {
        promises.push(axios.post(`${API}/pickup-safety/billing-records?organizationId=${user.organizationId}&userId=${user.id}`, {
          childIds: smsChildIds,
          alertType: 'sms',
          billingPeriod: billingPeriod
        }));
      }
      
      await Promise.all(promises);
      
      // Refresh billing records
      const billingRes = await axios.get(`${API}/pickup-safety/billing-records?organizationId=${user.organizationId}&billingPeriod=${billingPeriod}`);
      setBilledChildren(billingRes.data.billedMap || {});
      setSelectedForBilling(new Set());
      
      toast.success(`Marked ${selectedForBilling.size} children as billed`);
    } catch (error) {
      console.error("Error marking as billed:", error);
      toast.error("Failed to mark as billed");
    } finally {
      setSavingBilling(false);
    }
  };

  const fetchBillingHistory = async () => {
    try {
      const response = await axios.get(`${API}/pickup-safety/billing-history?organizationId=${user.organizationId}&limit=12`);
      setBillingHistory(response.data.history || []);
      setShowBillingHistory(true);
    } catch (error) {
      console.error("Error fetching billing history:", error);
      toast.error("Failed to load billing history");
    }
  };

  const handleGenerateInvoices = async () => {
    // Check if there are unbilled children
    const unbilledEmail = billableChildren.emailAlertChildren?.filter(c => !billedChildren[`${c.id}_email`]) || [];
    const unbilledSms = billableChildren.smsAlertChildren?.filter(c => !billedChildren[`${c.id}_sms`]) || [];
    
    if (unbilledEmail.length === 0 && unbilledSms.length === 0) {
      toast.info('All children have already been billed for this period');
      return;
    }
    
    // Calculate total amount
    const totalAmount = (unbilledEmail.length * billingSettings.emailFeePerChild) + 
                        (unbilledSms.length * billingSettings.smsFeePerChild);
    
    // Build detailed confirmation message
    const confirmMessage = `⚠️ Are you sure you want to send invoices?\n\n` +
      `📧 Email Alerts: ${unbilledEmail.length} children\n` +
      `📱 SMS Alerts: ${unbilledSms.length} children\n` +
      `💰 Total Amount: $${totalAmount.toFixed(2)}\n` +
      `📅 Due Date: ${invoiceDueDate}\n\n` +
      `This will:\n` +
      `• Create invoices for parents\n` +
      `• Send emails with payment instructions\n` +
      `• Mark children as billed for ${formatBillingPeriod(billingPeriod)}\n\n` +
      `Continue?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      setGeneratingInvoices(true);
      
      const response = await axios.post(`${API}/pickup-safety/generate-invoices?organizationId=${user.organizationId}&userId=${user.id}`, {
        billingPeriod: billingPeriod,
        sendEmails: true,
        dueDate: invoiceDueDate,
        notes: `Monthly Pick Up Safety Alert fees for ${formatBillingPeriod(billingPeriod)}`
      });
      
      if (response.data.success) {
        toast.success(`Created ${response.data.invoicesCreated} invoices, sent ${response.data.emailsSent} emails`);
        
        // Refresh billing records
        const billingRes = await axios.get(`${API}/pickup-safety/billing-records?organizationId=${user.organizationId}&billingPeriod=${billingPeriod}`);
        setBilledChildren(billingRes.data.billedMap || {});
        
        // Refresh unpaid invoices
        fetchUnpaidInvoices();
      }
    } catch (error) {
      console.error("Error generating invoices:", error);
      toast.error(error.response?.data?.detail || "Failed to generate invoices");
    } finally {
      setGeneratingInvoices(false);
    }
  };

  const fetchUnpaidInvoices = async () => {
    try {
      const response = await axios.get(`${API}/pickup-safety/unpaid-invoices?organizationId=${user.organizationId}`);
      setUnpaidInvoices(response.data);
    } catch (error) {
      console.error("Error fetching unpaid invoices:", error);
    }
  };

  const handleSendReminders = async () => {
    if (unpaidInvoices.total === 0) {
      toast.info('No unpaid invoices to send reminders for');
      return;
    }
    
    if (!window.confirm(`Send payment reminders to ${unpaidInvoices.total} parents with unpaid invoices?`)) {
      return;
    }
    
    try {
      setSendingReminders(true);
      const response = await axios.post(`${API}/pickup-safety/send-payment-reminders?organizationId=${user.organizationId}`, {
        daysOverdue: 3,
        includeNoDueDate: true
      });
      
      if (response.data.success) {
        toast.success(`Sent ${response.data.remindersSent} payment reminders`);
      }
    } catch (error) {
      console.error("Error sending reminders:", error);
      toast.error("Failed to send reminders");
    } finally {
      setSendingReminders(false);
    }
  };

  const exportToCSV = () => {
    const rows = [['Child Name', 'Alert Type', 'Parent Contact', 'Fee', 'Billed Status', 'Billing Period']];
    
    billableChildren.emailAlertChildren?.forEach(child => {
      const key = `${child.id}_email`;
      rows.push([
        child.name,
        'Email',
        child.pickupAlerts?.alertEmail || child.pickupPersons?.[0]?.email || '',
        `$${billingSettings.emailFeePerChild.toFixed(2)}`,
        billedChildren[key] ? 'Billed' : 'Not Billed',
        billingPeriod
      ]);
    });
    
    billableChildren.smsAlertChildren?.forEach(child => {
      const key = `${child.id}_sms`;
      rows.push([
        child.name,
        'SMS',
        child.pickupAlerts?.alertPhone || child.pickupPersons?.[0]?.phone || '',
        `$${billingSettings.smsFeePerChild.toFixed(2)}`,
        billedChildren[key] ? 'Billed' : 'Not Billed',
        billingPeriod
      ]);
    });
    
    const csvContent = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pickup-safety-billing-${billingPeriod}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Billing list exported to CSV');
  };

  const printBillingList = () => {
    // Count billed items
    const emailBilledCount = billableChildren.emailAlertChildren?.filter(c => billedChildren[`${c.id}_email`]).length || 0;
    const smsBilledCount = billableChildren.smsAlertChildren?.filter(c => billedChildren[`${c.id}_sms`]).length || 0;
    
    const printContent = `
      <html>
        <head>
          <title>Pick Up Safety Alerts - Billing List</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #d97706; border-bottom: 2px solid #d97706; padding-bottom: 10px; }
            .period-badge { background: #fef3c7; padding: 4px 12px; border-radius: 20px; font-size: 14px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
            th { background: #fef3c7; color: #92400e; }
            .billed { background: #d1fae5; }
            .total { font-size: 18px; font-weight: bold; margin-top: 20px; text-align: right; }
            .section { margin-top: 30px; }
            .section-title { color: #065f46; font-size: 16px; margin-bottom: 10px; }
            .summary { background: #f0fdf4; padding: 10px; border-radius: 8px; margin-top: 10px; }
            @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          <h1>Pick Up Safety Alerts - Billing Report</h1>
          <p>Generated: ${new Date().toLocaleDateString()} | <span class="period-badge">Billing Period: ${formatBillingPeriod(billingPeriod)}</span> | Frequency: ${billingSettings.billingFrequency}</p>
          
          ${billableChildren.emailAlertChildren?.length > 0 ? `
            <div class="section">
              <div class="section-title">📧 Email Alerts (${billableChildren.emailAlertChildren.length} children) - $${billingSettings.emailFeePerChild.toFixed(2)}/child</div>
              <table>
                <tr><th>Status</th><th>Child Name</th><th>Parent Email</th><th>Fee</th></tr>
                ${billableChildren.emailAlertChildren.map(child => `
                  <tr class="${billedChildren[`${child.id}_email`] ? 'billed' : ''}">
                    <td>${billedChildren[`${child.id}_email`] ? '✅ Billed' : '⬜ Pending'}</td>
                    <td>${child.name}</td>
                    <td>${child.pickupAlerts?.alertEmail || child.pickupPersons?.[0]?.email || '-'}</td>
                    <td>$${billingSettings.emailFeePerChild.toFixed(2)}</td>
                  </tr>
                `).join('')}
              </table>
              <div class="summary">
                <strong>Email Summary:</strong> ${emailBilledCount} of ${billableChildren.emailAlertChildren.length} billed
              </div>
            </div>
          ` : ''}
          
          ${billableChildren.smsAlertChildren?.length > 0 ? `
            <div class="section">
              <div class="section-title">📱 SMS Alerts (${billableChildren.smsAlertChildren.length} children) - $${billingSettings.smsFeePerChild.toFixed(2)}/child</div>
              <table>
                <tr><th>Status</th><th>Child Name</th><th>Parent Phone</th><th>Fee</th></tr>
                ${billableChildren.smsAlertChildren.map(child => `
                  <tr class="${billedChildren[`${child.id}_sms`] ? 'billed' : ''}">
                    <td>${billedChildren[`${child.id}_sms`] ? '✅ Billed' : '⬜ Pending'}</td>
                    <td>${child.name}</td>
                    <td>${child.pickupAlerts?.alertPhone || child.pickupPersons?.[0]?.phone || '-'}</td>
                    <td>$${billingSettings.smsFeePerChild.toFixed(2)}</td>
                  </tr>
                `).join('')}
              </table>
              <div class="summary">
                <strong>SMS Summary:</strong> ${smsBilledCount} of ${billableChildren.smsAlertChildren.length} billed
              </div>
            </div>
          ` : ''}
          
          <div class="total">
            Total Monthly Billing: $${(
              (billableChildren.emailAlertChildren?.length || 0) * billingSettings.emailFeePerChild +
              (billableChildren.smsAlertChildren?.length || 0) * billingSettings.smsFeePerChild
            ).toFixed(2)}
          </div>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handleUpdateDefaults = async (field, value) => {
    try {
      const updatedSettings = { ...settings, [field]: value };
      await axios.put(`${API}/pickup-safety/settings?organizationId=${user.organizationId}`, updatedSettings);
      setSettings(updatedSettings);
      toast.success("Default settings updated");
    } catch (error) {
      console.error("Error updating settings:", error);
      toast.error("Failed to update settings");
    }
  };

  const handleBulkEnable = async (alertType, enable) => {
    try {
      setBulkLoading(true);
      const response = await axios.post(
        `${API}/pickup-safety/bulk-enable?organizationId=${user.organizationId}&alertType=${alertType}&enable=${enable}`
      );
      toast.success(response.data.message);
      fetchData(); // Refresh stats
    } catch (error) {
      console.error("Error bulk enabling:", error);
      toast.error(error.response?.data?.detail || "Failed to update");
    } finally {
      setBulkLoading(false);
    }
  };

  // Verify SMS Setup — sends a sample pickup alert SMS to confirm Twilio works
  const handleSendTestSms = async () => {
    const phone = (testSmsPhone || "").trim();
    if (!phone) {
      toast.error("Enter a phone number first");
      return;
    }
    try {
      setSendingTestSms(true);
      const res = await axios.post(`${API}/pickup-safety/test-sms`, { phone });
      const to = res.data?.to || phone;
      toast.success(`Test SMS sent to ${to}`);
      // Refresh settings so the "Last test" line updates
      const updated = await axios.get(
        `${API}/pickup-safety/settings?organizationId=${user.organizationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings(prev => ({ ...prev, ...updated.data }));
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to send test SMS";
      toast.error(msg, { duration: 12000 });
    } finally {
      setSendingTestSms(false);
    }
  };

  // Check for success/canceled URL params (Square redirect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      // Verify payment with backend
      const verifyPayment = async () => {
        try {
          const response = await axios.post(`${API}/pickup-safety/verify-payment?organizationId=${user?.organizationId}`);
          if (response.data.activated) {
            toast.success("SMS Alerts subscription activated!");
          } else {
            toast.info("Payment verification in progress. Please refresh in a moment.");
          }
          fetchData();
        } catch (error) {
          console.error("Error verifying payment:", error);
        }
      };
      if (user?.organizationId) {
        verifyPayment();
      }
      // Clean URL
      window.history.replaceState({}, '', '/pickup-safety');
    } else if (params.get('canceled') === 'true') {
      toast.info("Subscription canceled");
      window.history.replaceState({}, '', '/pickup-safety');
    }
  }, [user?.organizationId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true
    });
  };

  // Format billing period from "2026-03" to "March 2026"
  const formatBillingPeriod = (period) => {
    if (!period) return "";
    const [year, month] = period.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <AppShell pageTitle="Pick Up Safety Alerts">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell pageTitle="Pick Up Safety Alerts" breadcrumb>
      {/* Hero Banner */}
      <PageHero 
        imageUrl={HERO_IMAGES.pickupSafety}
        title="Pick Up Safety Alerts"
        subtitle="Secure child release verification and parent notifications"
        altText="Safe child pickup verification"
      />
      
      <FeatureGate feature="pickup_safety_email">
      <div className="relative z-10 space-y-6" data-testid="pickup-safety-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pick Up Safety Alerts</h1>
            <p className="text-gray-600">Keep parents informed when alternate pickups occur</p>
          </div>
        </div>

        {/* ── SUBSCRIPTION GATE ── Always at top ── */}
        <Card ref={subscriptionSectionRef} className="border-2 border-blue-300 bg-gradient-to-br from-blue-50 via-white to-purple-50" data-testid="sms-subscription-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <Phone className="w-5 h-5" />
              Subscribe to SMS Alerts
            </CardTitle>
            <CardDescription>
              Send instant text messages to parents when someone other than the primary pickup person picks up their child
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">

            {/* ── OPTION A: Director Notice — What parents will receive ── */}
            <DirectorNoticePanel orgName={settings.organizationName || organization?.name || 'Your Center'} />

            {/* Plan Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`p-4 rounded-xl border-2 cursor-pointer transition-all relative ${selectedPlan === 'monthly' ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-gray-200 hover:border-blue-300'}`} onClick={() => setSelectedPlan('monthly')}>
                <Badge className="absolute -top-2 left-4 bg-amber-500 text-white">50% OFF!</Badge>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-gray-900">Monthly</span>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPlan === 'monthly' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                    {selectedPlan === 'monthly' && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <p className="text-3xl font-bold text-gray-900"><span className="line-through text-gray-400 text-xl mr-2">$29.99</span>$14.99<span className="text-base font-normal text-gray-500">/month</span></p>
                <p className="text-sm text-blue-600 font-medium mt-1">50% off — limited time</p>
                <p className="text-sm text-green-600 font-medium mt-1">Limited time offer!</p>
              </div>
              <div className={`p-4 rounded-xl border-2 cursor-pointer transition-all relative ${selectedPlan === 'annual' ? 'border-green-500 bg-green-50 shadow-md' : 'border-gray-200 hover:border-green-300'}`} onClick={() => setSelectedPlan('annual')}>
                <Badge className="absolute -top-2 -right-2 bg-green-500 text-white">Best Value!</Badge>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-gray-900">Annual</span>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPlan === 'annual' ? 'border-green-500 bg-green-500' : 'border-gray-300'}`}>
                    {selectedPlan === 'annual' && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <p className="text-3xl font-bold text-gray-900"><span className="line-through text-gray-400 text-xl mr-2">$359.88</span>$161.80<span className="text-base font-normal text-gray-500">/year</span></p>
                <p className="text-sm text-green-600 font-medium mt-1">Save $198.08 vs full price — 10% off annual</p>
              </div>
            </div>

            {/* Email warning - always show */}
            {user?.email && (
              <div className="rounded-xl border border-blue-300 bg-blue-50 p-4 space-y-2">
                <p className="flex items-center gap-2 text-sm font-bold text-blue-800"><AlertTriangle className="h-4 w-4" /> Important — use this email at Square checkout</p>
                <p className="text-sm text-blue-700">When prompted for an email on Square, you <strong>MUST</strong> enter:</p>
                <p className="rounded-lg bg-white px-3 py-2 text-sm font-bold text-blue-900 ring-1 ring-blue-300">{user.email}</p>
                <p className="text-xs text-blue-600">This links your subscription to your account. A different email will delay activation.</p>
              </div>
            )}

            {/* Subscribe Button - always visible */}
            <Button onClick={handleSubscribe} disabled={subscribing} className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-6 text-lg font-semibold rounded-xl shadow-lg" data-testid="subscribe-sms-btn">
              {subscribing ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</> : <><CreditCard className="w-5 h-5 mr-2" />Subscribe Now - {selectedPlan === 'monthly' ? '$14.99/month' : '$161.80/year'}</>}
            </Button>
            <p className="text-center text-sm text-gray-500">After paying on Square, return here and click the <strong>refresh button at the top of the page</strong> to activate.</p>

            {/* Active state shown below pay button */}
            {settings.smsSubscribed && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <p className="font-semibold text-green-800">SMS Subscription Active</p>
                </div>
                <Button onClick={handleCancelSubscription} disabled={canceling} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 flex-shrink-0" data-testid="cancel-subscription-btn">
                  {canceling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Canceling...</> : 'Cancel at anytime'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lock banner — matches Parent Communication SMS style, primary owner always bypasses */}
        {!settings.smsSubscribed && !isPrimaryOwner && (
          <div className="rounded-2xl overflow-hidden border-2 border-orange-300 shadow-lg">
            <div className="bg-gradient-to-r from-orange-500 via-rose-500 to-pink-500 px-6 py-5 text-white">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Subscribe above to activate Pickup Safety Alerts</h3>
                    <p className="text-white/90 text-sm mt-0.5">All settings and features below will unlock immediately after payment</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-black">$14.99</span>
                  <span className="text-white/80 text-sm">/month</span>
                  <p className="text-white/70 text-xs">or $161.80/year</p>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                {['Instant SMS to parents','Alternate pickup alerts','Email notifications','Consent management'].map(f => (
                  <div key={f} className="flex items-center gap-1.5 text-xs text-white/90">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-300 flex-shrink-0" />
                    {f}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* All content below — always visible, dimmed for unsubscribed directors, primary owner always full access */}
        <div className={(!settings.smsSubscribed && !isPrimaryOwner) ? 'opacity-40 pointer-events-none select-none' : ''}>
        <div className="space-y-6">

        {/* Subscription Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Email Status Card */}
          <Card className={`border-2 ${settings.emailSubscribed ? 'border-green-200 bg-gradient-to-br from-green-50 to-white' : 'border-gray-200 bg-gray-50'}`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${settings.emailSubscribed ? 'bg-green-100' : 'bg-gray-200'}`}>
                    <Mail className={`w-6 h-6 ${settings.emailSubscribed ? 'text-green-600' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Email Alerts</p>
                    <p className="text-sm text-gray-500">
                      {billingSettings.chargeParents && billingSettings.emailFeePerChild > 0
                        ? `$${parseFloat(billingSettings.emailFeePerChild).toFixed(2)}/child/month`
                        : 'Currently free'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {settings.emailSubscribed ? (
                    <Badge className="bg-green-100 text-green-700 border-green-200">
                      <Check className="w-3 h-3 mr-1" />
                      Active
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-gray-500">
                      <Lock className="w-3 h-3 mr-1" />
                      Off
                    </Badge>
                  )}
                  <Switch
                    checked={settings.emailSubscribed}
                    onCheckedChange={handleToggleEmail}
                    data-testid="email-subscription-toggle"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SMS Status Card */}
          <Card className={`border-2 ${settings.smsSubscribed ? 'border-blue-200 bg-gradient-to-br from-blue-50 to-white' : 'border-gray-200 bg-gray-50'}`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${settings.smsSubscribed ? 'bg-blue-100' : 'bg-gray-200'}`}>
                    <Phone className={`w-6 h-6 ${settings.smsSubscribed ? 'text-blue-600' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">SMS Alerts</p>
                    <p className="text-sm text-gray-500">
                      {settings.smsSubscribed 
                        ? (billingSettings.chargeParents && billingSettings.smsFeePerChild > 0
                            ? `$${parseFloat(billingSettings.smsFeePerChild).toFixed(2)}/child/month`
                            : (settings.smsSubscriptionEndDate 
                                ? `Renews ${formatDate(settings.smsSubscriptionEndDate)}`
                                : 'Permanent Access - No Expiration'))
                        : '$14.99/month or $161.80/year'
                      }
                    </p>
                  </div>
                </div>
                {settings.smsSubscribed ? (
                  <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                    <Check className="w-3 h-3 mr-1" />
                    {settings.smsSubscriptionEndDate ? 'Active' : 'Lifetime'}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-gray-500">
                    <Lock className="w-3 h-3 mr-1" />
                    Not Active
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </div>


        {/* Default Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Default Settings
            </CardTitle>
            <CardDescription>
              Auto-enable alerts for newly added children
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Auto-enable Email Alerts</p>
                <p className="text-sm text-gray-500">New children will have email alerts enabled by default</p>
              </div>
              <Switch
                checked={settings.defaultEmailAlertEnabled}
                onCheckedChange={(checked) => handleUpdateDefaults('defaultEmailAlertEnabled', checked)}
                data-testid="default-email-toggle"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className={`font-medium ${settings.smsSubscribed ? 'text-gray-900' : 'text-gray-400'}`}>
                  Auto-enable SMS Alerts
                </p>
                <p className="text-sm text-gray-500">
                  {settings.smsSubscribed 
                    ? 'New children will have SMS alerts enabled by default'
                    : 'Requires SMS subscription'
                  }
                </p>
              </div>
              {settings.smsSubscribed ? (
                <Switch
                  checked={settings.defaultSmsAlertEnabled}
                  onCheckedChange={(checked) => handleUpdateDefaults('defaultSmsAlertEnabled', checked)}
                  data-testid="default-sms-toggle"
                />
              ) : (
                <Lock className="w-5 h-5 text-gray-300" />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Verify SMS Setup — sends a sample pickup alert for setup confirmation */}
        {(user?.role?.toLowerCase() === 'owner' || user?.role?.toLowerCase() === 'co-owner') && (
          <Card className="border-2 border-amber-200" data-testid="pickup-test-sms-card">
            <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50">
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <Send className="w-5 h-5" />
                Verify SMS Setup
              </CardTitle>
              <CardDescription>
                Send a sample pickup alert to your cell to confirm Twilio + carrier delivery is working.
                Use this when troubleshooting "I'm not getting alerts."
              </CardDescription>
              <p className="text-xs italic text-amber-700 mt-2" data-testid="pickup-safety-usage-note">
                Designed for normal safety alert usage. High-volume use may require review.
              </p>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cell phone</label>
                  <input
                    type="tel"
                    placeholder="(412) 555-1234"
                    value={testSmsPhone}
                    onChange={(e) => setTestSmsPhone(e.target.value)}
                    data-testid="test-sms-phone-input"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSendTestSms}
                  disabled={
                    sendingTestSms
                    || !testSmsPhone.trim()
                    || ((settings.testSmsCount ?? 0) >= (settings.testSmsMonthlyLimit ?? 5))
                  }
                  data-testid="send-test-sms-btn"
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-md font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingTestSms ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sendingTestSms ? "Sending..." : "Send Test SMS"}
                </button>
              </div>
              {settings.lastTestSmsAt && (
                <div className="text-sm text-gray-600 flex items-center gap-2" data-testid="last-test-sms-info">
                  <Clock className="w-4 h-4" />
                  Last test:{' '}
                  {new Date(settings.lastTestSmsAt).toLocaleString()}
                  {settings.lastTestSmsTo && <> &middot; {settings.lastTestSmsTo}</>}
                  {settings.lastTestSmsStatus === 'delivered'
                    ? <span className="text-green-600 font-medium ml-1">&check; Delivered</span>
                    : settings.lastTestSmsStatus === 'failed'
                      ? <span className="text-red-600 font-medium ml-1">&times; Failed</span>
                      : null}
                </div>
              )}
              <div
                className="text-sm font-medium text-gray-700"
                data-testid="test-sms-counter"
              >
                Tests this month: {settings.testSmsCount ?? 0} / {settings.testSmsMonthlyLimit ?? 5}
              </div>
              <p className="text-xs text-gray-500">
                Monthly limit resets on the 1st. Need a reset sooner? Email{' '}
                <a href="mailto:admin@todayskidzsimplesignin.com" className="text-amber-700 underline">
                  admin@todayskidzsimplesignin.com
                </a>{' '}
                for assistance.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Parent Billing Setup */}
        <Card className={`border-2 ${canActivateParentBilling ? 'border-blue-200' : 'border-slate-200 bg-slate-50/70'}`} data-testid="pickup-parent-billing-card">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <DollarSign className="w-5 h-5" />
              Parent Billing Setup
            </CardTitle>
            <CardDescription>
              Set up fees to charge parents for Pick Up Safety Alerts after the center subscription is active
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {/* Enable Parent Charging */}
            <div className={`flex items-center justify-between p-4 rounded-lg border ${canActivateParentBilling ? 'bg-blue-50 border-blue-200' : 'bg-slate-100 border-slate-200'}`}>
              <div>
                <p className={`font-medium ${canActivateParentBilling ? 'text-gray-900' : 'text-slate-500'}`}>Charge Parents for Alerts</p>
                <p className="text-sm text-gray-500">
                  {canActivateParentBilling
                    ? 'Enable this to set up parent billing for this feature'
                    : 'Complete the center subscription first, then parent billing becomes available'
                  }
                </p>
              </div>
              {canActivateParentBilling ? (
                <Switch
                  checked={billingSettings.chargeParents}
                  onCheckedChange={(checked) => handleBillingChange('chargeParents', checked)}
                  data-testid="charge-parents-toggle"
                />
              ) : (
                <div className="flex items-center gap-2 text-slate-400" data-testid="charge-parents-locked-state">
                  <Lock className="h-5 w-5" />
                  <span className="text-sm font-medium">Locked</span>
                </div>
              )}
            </div>

            {!canActivateParentBilling && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4" data-testid="pickup-parent-billing-subscribe-gate">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-semibold text-blue-900">Center payment comes first</p>
                    <p className="text-sm text-blue-700">
                      The center must activate the $14.99/month or annual SMS Alerts subscription before parent billing controls can be turned on.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={scrollToSubscriptionSection}
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    data-testid="pickup-parent-billing-subscribe-button"
                  >
                    <CreditCard className="mr-2 h-4 w-4" />
                    Go to center payment
                  </Button>
                </div>
              </div>
            )}
            
            {canActivateParentBilling && billingSettings.chargeParents && (
              <>
                {/* Fee Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Email Fee */}
                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Mail className="w-5 h-5 text-green-600" />
                      <label className="font-medium text-gray-800">Email Alert Fee</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.50"
                        value={billingSettings.emailFeePerChild === 0 ? '' : billingSettings.emailFeePerChild}
                        onChange={(e) => {
                          const val = e.target.value;
                          handleBillingChange('emailFeePerChild', val === '' ? 0 : parseFloat(val));
                        }}
                        onBlur={(e) => {
                          if (e.target.value === '') {
                            handleBillingChange('emailFeePerChild', 0);
                          }
                        }}
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                      />
                      <span className="text-gray-500">/ child / month</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Set to $0 for free</p>
                  </div>
                  
                  {/* SMS Fee */}
                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Phone className="w-5 h-5 text-blue-600" />
                      <label className="font-medium text-gray-800">SMS Alert Fee</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.50"
                        value={billingSettings.smsFeePerChild === 0 ? '' : billingSettings.smsFeePerChild}
                        onChange={(e) => {
                          const val = e.target.value;
                          handleBillingChange('smsFeePerChild', val === '' ? 0 : parseFloat(val));
                        }}
                        onBlur={(e) => {
                          if (e.target.value === '') {
                            handleBillingChange('smsFeePerChild', 0);
                          }
                        }}
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                      />
                      <span className="text-gray-500">/ child / month</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Set to $0 for free.</p>
                  </div>
                </div>
                
                {/* Billing Frequency */}
                <div className="p-4 bg-white rounded-lg border border-gray-200">
                  <label className="font-medium text-gray-800 block mb-3">Billing Frequency</label>
                  <div className="flex gap-3">
                    {['monthly', 'annually'].map((freq) => (
                      <button
                        key={freq}
                        onClick={() => handleBillingChange('billingFrequency', freq)}
                        className={`px-4 py-2 rounded-lg border-2 transition-all capitalize ${
                          billingSettings.billingFrequency === freq
                            ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                            : 'border-gray-200 text-gray-600 hover:border-blue-300'
                        }`}
                      >
                        {freq}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Billing Day + Mode */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                    <label className="font-medium text-gray-800 block mb-3">Billing Day of Month</label>
                    <select value={billingSettings.billingDay || 1} onChange={e => setBillingSettings(p => ({...p, billingDay: parseInt(e.target.value)}))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500">
                      {Array.from({length:28},(_,i)=>i+1).map(d => <option key={d} value={d}>{d === 1 ? '1st' : d === 2 ? '2nd' : d === 3 ? '3rd' : `${d}th`} of the month</option>)}
                    </select>
                    <p className="text-xs text-gray-400 mt-1">Director gets reminder day before</p>
                  </div>
                  <div className="p-4 bg-white rounded-lg border border-gray-200">
                    <label className="font-medium text-gray-800 block mb-3">Send Mode</label>
                    <div className="space-y-2">
                      {[{val:'auto',label:'Auto-Send',desc:'Sends automatically on billing day'},{val:'manual',label:'Manual Approval',desc:'Queue until you approve'}].map(m => (
                        <label key={m.val} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border ${billingSettings.billingMode === m.val ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`}>
                          <input type="radio" checked={billingSettings.billingMode === m.val} onChange={() => setBillingSettings(p => ({...p, billingMode: m.val}))} className="accent-blue-600" />
                          <div><p className="text-xs font-semibold text-gray-800">{m.label}</p><p className="text-xs text-gray-400">{m.desc}</p></div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Fee Passthrough Toggle */}
                {(() => {
                  const base = parseFloat(billingSettings.smsFeePerChild) || 0;
                  const squareFee = +(base * 0.029 + 0.30).toFixed(2);
                  const platformFee = 5.00;
                  const parentPays = billingSettings.feePassthrough ? +(base + squareFee + platformFee).toFixed(2) : base;
                  const centerNets = billingSettings.feePassthrough ? base : +(base - squareFee - platformFee).toFixed(2);
                  return (
                    <div className="p-4 bg-white rounded-lg border-2 border-blue-200 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-gray-800">Pass Fees to Parent</p>
                          <p className="text-xs text-gray-500">Square processing fee + $5 platform fee</p>
                        </div>
                        <button type="button"
                          onClick={() => setBillingSettings(p => ({...p, feePassthrough: !p.feePassthrough}))}
                          className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${billingSettings.feePassthrough ? 'bg-blue-600' : 'bg-gray-300'}`}>
                          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${billingSettings.feePassthrough ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      <div className={`rounded-lg p-3 text-xs space-y-1 ${billingSettings.feePassthrough ? 'bg-blue-50 border border-blue-200' : 'bg-orange-50 border border-orange-200'}`}>
                        <p className="font-bold text-gray-700 mb-2">{billingSettings.feePassthrough ? '✓ Pass fees to parent' : '✓ Center absorbs fees'} — Example at ${base.toFixed(2)}/child:</p>
                        <div className="space-y-1 font-mono text-gray-600">
                          <div className="flex justify-between"><span>Your SMS fee</span><span>${base.toFixed(2)}</span></div>
                          <div className={`flex justify-between ${billingSettings.feePassthrough ? 'text-orange-600' : 'text-gray-400 line-through'}`}><span>Square processing (2.9%+$0.30)</span><span>+${squareFee.toFixed(2)}</span></div>
                          <div className={`flex justify-between ${billingSettings.feePassthrough ? 'text-orange-600' : 'text-gray-400 line-through'}`}><span>Platform fee</span><span>+$5.00</span></div>
                          <div className="border-t pt-1 mt-1 flex justify-between font-bold text-gray-800"><span>Parent pays</span><span>${parentPays.toFixed(2)}</span></div>
                          <div className={`flex justify-between font-bold ${centerNets < 0 ? 'text-red-600' : 'text-green-700'}`}><span>You receive</span><span>${centerNets.toFixed(2)}</span></div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Revenue Calculator — based on "you receive" amount */}
                {(() => {
                  const emailBase = parseFloat(billingSettings.emailFeePerChild) || 0;
                  const smsBase = parseFloat(billingSettings.smsFeePerChild) || 0;
                  const calcNet = (base) => billingSettings.feePassthrough ? base : +(base - (base * 0.029 + 0.30) - 5).toFixed(2);
                  const emailNet = calcNet(emailBase);
                  const smsNet = calcNet(smsBase);
                  const n = parseInt(estimateN) || stats.totalChildren || 0;
                  const emailActive = stats.childrenWithEmailAlert || 0;
                  const smsActive = stats.childrenWithSmsAlert || 0;
                  return (
                    <div className="rounded-xl border border-green-200 bg-green-50/40 p-3 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-semibold text-green-800">Estimated Revenue (what you receive)</p>
                        <div className="flex items-center gap-1.5">
                          <label className="text-xs text-green-700">Estimate for:</label>
                          <input type="number" min="1" value={estimateN}
                            onChange={e => setEstimateN(e.target.value)}
                            placeholder={String(stats.totalChildren || 0)}
                            className="w-16 h-7 border border-green-300 rounded-lg px-2 text-xs bg-white text-center focus:ring-1 focus:ring-green-400" />
                          <span className="text-xs text-green-600">children</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-white rounded-lg p-2 border border-green-200 text-center">
                          <p className="text-xs text-gray-500 mb-1">Email ({emailActive} active)</p>
                          <p className="text-base font-bold text-green-700">${(emailActive * emailNet).toFixed(2)}/mo</p>
                          <p className="text-xs text-green-600">${(emailActive * emailNet * 12).toFixed(2)}/yr</p>
                        </div>
                        <div className="bg-white rounded-lg p-2 border border-green-200 text-center">
                          <p className="text-xs text-gray-500 mb-1">SMS ({smsActive} active)</p>
                          <p className="text-base font-bold text-green-700">${(smsActive * smsNet).toFixed(2)}/mo</p>
                          <p className="text-xs text-green-600">${(smsActive * smsNet * 12).toFixed(2)}/yr</p>
                        </div>
                        <div className="bg-emerald-100 rounded-lg p-2 border border-emerald-300 text-center col-span-2">
                          <p className="text-xs text-emerald-700 font-semibold mb-1">Potential (all {n} children enrolled)</p>
                          <p className="text-base font-bold text-emerald-800">${(n * (emailNet + smsNet)).toFixed(2)}/mo · ${(n * (emailNet + smsNet) * 12).toFixed(2)}/yr</p>
                        </div>
                      </div>
                      {(emailNet < 0 || smsNet < 0) && <p className="text-xs text-red-600 font-medium">⚠️ Fee too low to cover costs — increase fee or enable fee passthrough.</p>}
                    </div>
                  );
                })()}
                
                {/* Payment Setup — original content moved inside billing card */}
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2">
                  <p className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Info className="h-4 w-4 text-blue-500 flex-shrink-0" /> How parent payments work</p>
                  <ul className="space-y-2 text-xs text-slate-600">
                    <li><span className="font-semibold text-blue-600">Zelle:</span> Payment link emails parent with your Zelle info. You verify receipt in your bank app, then mark the child's alert as active.</li>
                    <li><span className="font-semibold text-amber-600">Square / Card:</span> Parent pays online. Money deposits directly to your Square bank account. Activates automatically via webhook.</li>
                    <li><span className="font-semibold text-emerald-600">Connecting Square:</span> Click "Connect Square Account" → Square login → click Allow → redirected back automatically.</li>
                  </ul>
                </div>

                <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4 space-y-3">
                  <p className="font-semibold text-blue-700 flex items-center gap-2"><DollarSign className="h-4 w-4" /> Zelle Payment Option</p>
                  <p className="text-sm text-slate-500">Enter the email OR phone number where parents should send Zelle payment.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-blue-700 text-xs font-semibold block mb-1">Zelle Email</label>
                      <input type="email" value={billingSettings.zelleEmail || ''} onChange={e => setBillingSettings(p => ({...p, zelleEmail: e.target.value}))}
                        placeholder="your-email@example.com" className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm bg-white" />
                    </div>
                    <div className="relative">
                      <div className="absolute -left-3 top-7 bg-blue-100 text-blue-700 text-xs font-bold px-1 rounded">OR</div>
                      <label className="text-blue-700 text-xs font-semibold block mb-1">Zelle Phone</label>
                      <input type="tel" value={billingSettings.zellePhone || ''} onChange={e => setBillingSettings(p => ({...p, zellePhone: e.target.value}))}
                        placeholder="412-555-1234" className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm bg-white" />
                    </div>
                  </div>
                </div>

                <div className={`rounded-xl border p-4 space-y-3 ${billingSettings.parentSquareEnabled ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200 bg-slate-50/40'}`}>
                  <div className="flex items-center justify-between">
                    <p className={`font-semibold flex items-center gap-2 ${billingSettings.parentSquareEnabled ? 'text-amber-700' : 'text-slate-500'}`}>
                      <CreditCard className="h-4 w-4" /> Credit Card Payment Option
                    </p>
                    <button type="button" onClick={() => setBillingSettings(p => ({...p, parentSquareEnabled: !p.parentSquareEnabled}))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${billingSettings.parentSquareEnabled ? 'bg-amber-500' : 'bg-slate-300'}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${billingSettings.parentSquareEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  {billingSettings.parentSquareEnabled && (!squareConnected ? (
                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 space-y-2">
                      <p className="text-rose-700 text-sm">⚠️ Square account not connected. Connect to enable credit card payments.</p>
                      <Button type="button" onClick={connectSquare} disabled={connectingSquare} className="bg-amber-500 hover:bg-amber-600 text-white text-sm">
                        {connectingSquare ? 'Connecting...' : <><CreditCard className="mr-2 h-4 w-4" /> Connect Square Account</>}
                      </Button>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                      <p className="text-emerald-700 text-sm">Square account connected. Parents can pay by card.</p>
                    </div>
                  ))}
                </div>

                {/* Save Button */}
                <Button onClick={handleSaveBillingSettings} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  Save Billing Settings
                </Button>

                {/* Send Invoices + Payment Tracker */}
                <PickupBillingInvoicePanel billingSettings={billingSettings} billableChildren={billableChildren} />
                
              </>
            )}

            {/* Individual Child Activation List - always visible, locked when not subscribed */}
            <div className="mt-4 overflow-hidden rounded-[1.4rem] border border-emerald-100 relative">
                  {/* Lock overlay for unpaid centers */}
                  {!canActivateParentBilling && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm rounded-[1.4rem]">
                      <div className="flex flex-col items-center gap-3 p-6 text-center">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        </div>
                        <p className="font-bold text-slate-800">Subscribe to unlock</p>
                        <p className="text-sm text-slate-500">Activate your Pick Up Safety Alerts subscription above to manage individual child alerts</p>
                        <button onClick={() => { window.scrollTo({top: 0, behavior: 'smooth'}); toast.info("Subscribe above to unlock Pick Up Safety Alerts"); }}
                          className="rounded-full bg-blue-500 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-600 transition">
                          Subscribe Now
                        </button>
                      </div>
                    </div>
                  )}
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 bg-emerald-50 border-b border-emerald-100 sticky top-0 z-10">
                      <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                        <Users className="w-5 h-5 text-blue-600" />
                        Individual Child Activation List
                      </h4>
                      <div className="flex items-center gap-2">
                        <button onClick={exportToCSV} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors">
                          <FileSpreadsheet className="w-4 h-4" /> Export CSV
                        </button>
                        <button onClick={printBillingList} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors">
                          <Printer className="w-4 h-4" /> Print
                        </button>
                      </div>
                    </div>

                    {/* Enrolled Children note */}
                    <div className="px-4 py-3 border-b border-cyan-100 bg-cyan-50 text-sm text-cyan-800">
                      Update email in <span className="font-semibold">Enrolled Children</span>, then hit the <span className="font-semibold">refresh</span> button at the top of the page to see changes.
                    </div>

                    {/* Column headers — removed Email Link and SMS Link columns */}
                    <div className="grid grid-cols-[1.3fr_1.4fr_100px_100px_110px] gap-2 bg-emerald-50 px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-600 border-b border-emerald-100">
                      <span>Child Name</span>
                      <span>Parent Email</span>
                      <span className="text-center">Email Alert</span>
                      <span className="text-center">SMS Alert</span>
                      <span className="text-center">Status</span>
                    </div>

                    {/* All children rows */}
                    <div className="max-h-[480px] overflow-y-auto">
                      {(billableChildren.allChildren?.length > 0 ? billableChildren.allChildren : []).map((child) => {
                        const emailActive = child.pickupAlerts?.emailAlertEnabled;
                        const smsActive = child.pickupAlerts?.smsAlertEnabled;
                        const parentEmail = child.pickupPersons?.[0]?.email || child.pickupAlerts?.alertEmail || '-';
                        // Fee reflects actual active services
                        const emailFee = emailActive ? (billingSettings.emailFeePerChild || 0) : 0;
                        const smsFee = smsActive ? (billingSettings.smsFeePerChild || 0) : 0;
                        const fee = emailFee + smsFee;
                        const isBilled = !!billedChildren[`${child.id}_email`] || !!billedChildren[`${child.id}_sms`];
                        const status = isBilled ? 'Billed' : (emailActive && smsActive) ? 'Both Active' : smsActive ? 'SMS Active' : emailActive ? 'Email Active' : 'Inactive';
                        const statusColor = isBilled
                          ? 'bg-green-100 text-green-700'
                          : (emailActive && smsActive)
                          ? 'bg-purple-100 text-purple-700'
                          : smsActive
                          ? 'bg-blue-100 text-blue-700'
                          : emailActive
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-100 text-slate-500';

                        const renderLink = (alertType) => {
                          const key = `${child.id}_${alertType}`;
                          const link = paymentLinks[key];
                          const isCreating = creatingLink === key;
                          const isActive = alertType === 'email' ? emailActive : smsActive;
                          if (!isActive) return <span className="text-[11px] text-slate-300">—</span>;
                          if (link?.linkUrl) {
                            return (
                              <div className="flex items-center justify-center gap-1">
                                <button onClick={() => navigator.clipboard.writeText(link.linkUrl).then(() => toast.success('Copied'))}
                                  className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50">
                                  <Copy className="h-3.5 w-3.5" />
                                </button>
                                <button onClick={() => window.open(link.linkUrl, '_blank')}
                                  className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50">
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            );
                          }
                          return (
                            <button onClick={() => createPaymentLink(child.id, alertType)} disabled={isCreating}
                              className="text-[11px] text-slate-400 hover:text-emerald-600 transition-colors disabled:opacity-50 whitespace-nowrap">
                              {isCreating ? 'Creating...' : 'Create invite first'}
                            </button>
                          );
                        };

                        return (
                          <div key={child.id} className="grid grid-cols-[1.3fr_1.4fr_100px_100px_110px] gap-2 border-b border-emerald-50 bg-white px-3 py-3 text-xs items-center last:border-0">
                            <div className="font-medium text-slate-900 truncate">{child.name}</div>
                            <div className="text-slate-500 truncate">{parentEmail}</div>
                            <div className="flex justify-center">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button onClick={() => {
                                      if (!canActivateParentBilling) { toast.info("Subscribe to Pick Up Safety Alerts to enable this feature"); return; }
                                      toggleChildAlert(child.id, 'email', emailActive, child);
                                    }}
                                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${emailActive ? 'bg-emerald-500' : 'bg-slate-300'} ${!canActivateParentBilling ? 'opacity-40 cursor-not-allowed' : ''}`}>
                                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${emailActive ? 'translate-x-4' : 'translate-x-1'}`} />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="max-w-xs text-xs p-3 bg-gray-900 text-white rounded-xl">
                                    <p className="font-bold mb-1">📧 Email Alert — What happens when enabled:</p>
                                    <p className="mb-1">A welcome email is sent to the parent's email on file explaining the service.</p>
                                    <p className="mb-1">When an alternate pickup occurs, the parent receives an <strong className="text-white">email notification</strong> with the pickup person's name and exact time.</p>
                                    <p className="text-gray-300 text-[10px]">Only fires for <strong className="text-white">authorized alternate pickup persons</strong> signing out {child.name}.</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                            <div className="flex justify-center">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button onClick={() => {
                                      if (!canActivateParentBilling) { toast.info("Subscribe to Pick Up Safety Alerts to enable this feature"); return; }
                                      toggleChildAlert(child.id, 'sms', smsActive, child);
                                    }}
                                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${smsActive ? 'bg-blue-500' : 'bg-slate-300'} ${!canActivateParentBilling ? 'opacity-40 cursor-not-allowed' : ''}`}>
                                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${smsActive ? 'translate-x-4' : 'translate-x-1'}`} />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="left" className="max-w-xs text-xs p-3 bg-gray-900 text-white rounded-xl">
                                    <p className="font-bold mb-1">🛡️ SMS Alert — What happens when enabled:</p>
                                    <p className="mb-1">A welcome email is sent to the parent explaining the service.</p>
                                    <p className="mb-1 font-mono bg-gray-800 rounded p-1 text-green-400 text-[10px]">
                                      "[Center]: {child.name} picked up by [Person] at [Time]. Reply STOP to opt out."
                                    </p>
                                    <p className="text-gray-300">This alert fires only when an <strong className="text-white">authorized alternate pickup person</strong> signs out {child.name}.</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                            <div className="flex justify-center">
                              <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusColor}`}>{status}</span>
                            </div>
                          </div>
                        );
                      })}
                      {billableChildren.allChildren?.length === 0 && (
                        <div className="text-center py-8 text-slate-400 text-sm">No enrolled children found.</div>
                      )}
                    </div>

                    {/* Mark selected billed footer */}
                    {selectedForBilling.size > 0 && (
                      <div className="flex items-center justify-between px-4 py-3 bg-blue-50 border-t border-blue-200">
                        <Button onClick={handleMarkSelectedAsBilled} disabled={savingBilling} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                          {savingBilling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                          Mark {selectedForBilling.size} as Billed
                        </Button>
                        <p className="text-sm font-bold text-blue-700">
                          {'Total: $'}{Array.from(selectedForBilling).reduce((sum, key) => {
                            return sum + (key.endsWith('_sms') ? billingSettings.smsFeePerChild : billingSettings.emailFeePerChild);
                          }, 0).toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>

            {canActivateParentBilling && !billingSettings.chargeParents && (
              <div className="text-center py-4 text-gray-500">
                <DollarSign className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p>Enable "Charge Parents" above to set up parent billing</p>
                <p className="text-sm mt-1">Turn Pick Up Safety Alerts into a revenue stream!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* This Month's Activity */}
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Activity Report
                </CardTitle>
                <CardDescription>
                  {stats.month ? new Date(stats.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Current month'}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="month"
                  defaultValue={new Date().toISOString().slice(0,7)}
                  onChange={async (e) => {
                    try {
                      const res = await axios.get(`${API}/pickup-safety/stats?organizationId=${user.organizationId}&month=${e.target.value}`);
                      setStats(res.data);
                    } catch {}
                  }}
                  className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-400"
                />
                <button
                  onClick={() => {
                    const month = stats.month || new Date().toISOString().slice(0,7);
                    const monthLabel = new Date(month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                    const csv = `Activity Report,${monthLabel}\nEmails Sent,${stats.emailsSent}\nSMS Sent,${stats.smsSent}\nTotal Alerts,${(stats.emailsSent||0)+(stats.smsSent||0)}`;
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `pickup-safety-${month}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                >
                  <Download className="w-4 h-4" /> CSV
                </button>
                <button
                  onClick={() => window.print()}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                >
                  <Printer className="w-4 h-4" /> Print
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center p-4 bg-green-50 rounded-xl">
                <Mail className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-green-700">{stats.emailsSent}</p>
                <p className="text-sm text-green-600">Emails Sent</p>
              </div>
              <div className={`text-center p-4 rounded-xl ${settings.smsSubscribed ? 'bg-blue-50' : 'bg-gray-50'}`}>
                <Phone className={`w-8 h-8 mx-auto mb-2 ${settings.smsSubscribed ? 'text-blue-600' : 'text-gray-400'}`} />
                <p className={`text-3xl font-bold ${settings.smsSubscribed ? 'text-blue-700' : 'text-gray-400'}`}>{stats.smsSent}</p>
                <p className={`text-sm ${settings.smsSubscribed ? 'text-blue-600' : 'text-gray-400'}`}>SMS Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notification History Report */}
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Notification History
                  {logsView === 'archived' && (
                    <span className="text-sm font-normal text-rose-600">— Archived</span>
                  )}
                </CardTitle>
                <CardDescription className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                  <button
                    type="button"
                    onClick={() => { setLogsView('active'); fetchLogs('active', logsMonth); }}
                    className="text-slate-700 hover:underline font-medium"
                    data-testid="logs-count-total"
                    title="Total alerts in this scope (includes archived)"
                  >
                    {logsCounts.total} total
                  </button>
                  <span className="text-slate-300">·</span>
                  <button
                    type="button"
                    onClick={() => { setLogsView('active'); fetchLogs('active', logsMonth); }}
                    className={`hover:underline font-medium ${logsView === 'active' ? 'text-emerald-700' : 'text-emerald-600'}`}
                    data-testid="logs-count-active"
                  >
                    {logsCounts.active} active
                  </button>
                  <span className="text-slate-300">·</span>
                  <button
                    type="button"
                    onClick={() => { setLogsView('archived'); fetchLogs('archived', logsMonth); }}
                    className={`hover:underline font-medium ${logsView === 'archived' ? 'text-rose-700' : 'text-rose-600'}`}
                    data-testid="logs-count-archived"
                  >
                    {logsCounts.archived} archived
                  </button>
                  <span className="text-slate-300">·</span>
                  <span className="text-slate-500">{logsMonth ? new Date(logsMonth + '-02').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'All time'}</span>
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* View toggle */}
                <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => { setLogsView('active'); fetchLogs('active', logsMonth); }}
                    className={`px-3 py-1.5 text-sm font-medium ${logsView === 'active' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                    data-testid="logs-view-active"
                  >
                    Active
                  </button>
                  <button
                    type="button"
                    onClick={() => { setLogsView('archived'); fetchLogs('archived', logsMonth); }}
                    className={`px-3 py-1.5 text-sm font-medium ${logsView === 'archived' ? 'bg-rose-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                    data-testid="logs-view-archived"
                  >
                    Archived ({logsCounts.archived})
                  </button>
                </div>
                <input
                  type="month"
                  value={logsMonth}
                  onChange={async (e) => {
                    const m = e.target.value;
                    setLogsMonth(m);
                    await fetchLogs(logsView, m);
                  }}
                  className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-400"
                  placeholder="All time"
                  data-testid="logs-month-filter"
                />
                <button
                  onClick={() => {
                    const headers = ['Date','Child','Pickup Person','Type','Recipient','Status','Archived'];
                    const rows = logs.map(l => [
                      l.sentAt ? new Date(l.sentAt).toLocaleDateString('en-US') : '',
                      l.childName || '',
                      l.pickupPerson || '',
                      l.notificationType || '',
                      l.recipient || '',
                      l.success ? 'Sent' : 'Failed',
                      l.archived ? 'Yes' : 'No'
                    ]);
                    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `pickup-safety-history.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                  data-testid="logs-export-csv"
                >
                  <Download className="w-4 h-4" /> CSV
                </button>
                <button
                  onClick={() => window.print()}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                  data-testid="logs-print"
                >
                  <Printer className="w-4 h-4" /> Print
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {logsView === 'archived' && (
              <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3 flex items-start justify-between gap-3" data-testid="archived-banner">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-rose-700">Archived Notifications ({logsCounts.archived})</p>
                    <p className="text-xs text-rose-600 mt-0.5">
                      These notification logs have been archived. You can re-establish them or permanently delete them. Permanently deleted logs cannot be recovered.
                    </p>
                  </div>
                </div>
                {logsCounts.archived > 0 && (
                  <button
                    type="button"
                    onClick={handleClearAllArchived}
                    disabled={clearingArchived}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors disabled:opacity-50 flex-shrink-0"
                    data-testid="logs-clear-all-archived"
                  >
                    {clearingArchived ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Clear All
                  </button>
                )}
              </div>
            )}
            {logs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ShieldCheck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{logsView === 'archived' ? 'No archived notifications' : 'No notifications sent yet'}</p>
                <p className="text-sm">
                  {logsView === 'archived'
                    ? 'Archived alerts will appear here.'
                    : 'Alerts will appear here when alternate pickups occur'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="pb-3 pr-4">Date & Time</th>
                      <th className="pb-3 pr-4">Child</th>
                      <th className="pb-3 pr-4">Pickup Person</th>
                      <th className="pb-3 pr-4">Type</th>
                      <th className="pb-3 pr-4">Sent To</th>
                      <th className="pb-3 pr-4">Status</th>
                      <th className="pb-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {logs.map((log) => (
                      <tr key={log.id} className={`hover:bg-slate-50 ${log.archived ? 'bg-rose-50/40' : ''}`} data-testid={`log-row-${log.id}`}>
                        <td className="py-2.5 pr-4 text-slate-600 whitespace-nowrap">{log.sentAt ? new Date(log.sentAt).toLocaleDateString('en-US', {month:'short',day:'numeric',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}</td>
                        <td className="py-2.5 pr-4 font-medium text-slate-800">{log.childName}</td>
                        <td className="py-2.5 pr-4 text-slate-600">{log.pickupPerson}</td>
                        <td className="py-2.5 pr-4">
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${log.notificationType === 'email' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                            {log.notificationType === 'email' ? <Mail className="w-3 h-3" /> : <Phone className="w-3 h-3" />}
                            {log.notificationType}
                          </span>
                        </td>
                        <td className="py-2.5 pr-4 text-slate-500 text-xs">{log.recipient}</td>
                        <td className="py-2.5 pr-4">
                          <Badge variant={log.success ? "default" : "destructive"} className="text-xs">
                            {log.success ? 'Sent' : 'Failed'}
                          </Badge>
                        </td>
                        <td className="py-2.5 text-right whitespace-nowrap">
                          {logsView === 'active' ? (
                            <button
                              type="button"
                              onClick={() => handleArchiveLog(log.id)}
                              disabled={logActionLoading === log.id}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-rose-600 hover:bg-rose-50 rounded transition-colors disabled:opacity-50"
                              title="Archive this notification"
                              data-testid={`log-archive-${log.id}`}
                            >
                              {logActionLoading === log.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
                              Archive
                            </button>
                          ) : (
                            <div className="inline-flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleRestoreLog(log.id)}
                                disabled={logActionLoading === log.id}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-emerald-700 border border-emerald-200 hover:bg-emerald-50 rounded transition-colors disabled:opacity-50"
                                title="Re-establish this notification"
                                data-testid={`log-restore-${log.id}`}
                              >
                                {logActionLoading === log.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                                Re-establish
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteLog(log.id)}
                                disabled={logActionLoading === log.id}
                                className="inline-flex items-center justify-center px-2 py-1 text-xs text-rose-600 border border-rose-200 hover:bg-rose-50 rounded transition-colors disabled:opacity-50"
                                title="Permanently delete"
                                data-testid={`log-delete-${log.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        </div>
        </div>{/* end dimmed wrapper */}
      </div>
      </FeatureGate>
    </AppShell>
  );
}
